import { useEffect, useRef, useState } from 'react';
import { App } from 'antd';
import { SkyButton } from '@skynet/sky-button';

const VERSION_CHECK_INTERVAL = 1000 * 60 * 2; // 2 minutes
const NOTIFICATON_DURATION = 0; // seconds

const onError = (error: unknown) => {
  if (error instanceof Error) {
    console.error('Error fetching version:', error);
  } else {
    console.error('Error fetching version:', String(error));
  }
};

export function useAppUpdate() {
  const versionRef = useRef<string | null>(null);
  const intervalRef = useRef<number | undefined>(undefined);
  const { notification } = App.useApp();
  const [updated, setUpdated] = useState(false);

  useEffect(() => {
    const stopFetching = () => {
      if (intervalRef.current !== undefined) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }
    };

    const parseVersion = (data: unknown): string => {
      const isVersionRecord = (v: unknown): v is { version?: unknown } =>
        typeof v === 'object' && v !== null && 'version' in v;
      if (isVersionRecord(data) && typeof data.version === 'string') {
        return data.version;
      }
      return 'unknown';
    };

    const fetchVersion = async () => {
      try {
        const response = await fetch(`/version.json?t=${Date.now()}`);
        if (!response.ok) {
          onError(new Error(`Failed to fetch version.json: ${response.status}`));
          stopFetching();
          return;
        }

        const data: unknown = await response.json();
        const version = parseVersion(data);

        if (!versionRef.current) {
          versionRef.current = version;
          return;
        }

        if (versionRef.current !== version) {
          versionRef.current = version;
          setUpdated(true);
          notification.info({
            title: 'New version available',
            description:
              'Please refresh the page to update the application, otherwise it may not work properly.',
            btn: (
              <SkyButton
                shape="round"
                onClick={() => {
                  window.location.reload();
                }}
              >
                Reload
              </SkyButton>
            ),
            duration: NOTIFICATON_DURATION,
          });
          window.localStorage.setItem('app-updated', 'true');
        }
      } catch (error) {
        onError(error);
        stopFetching();
      }
    };

    if (intervalRef.current === undefined) {
      fetchVersion().catch(onError);
      intervalRef.current = window.setInterval(() => {
        fetchVersion().catch(onError);
      }, VERSION_CHECK_INTERVAL);
    }

    return () => {
      stopFetching();
    };
  }, [notification]);

  return { isAppUpdated: updated };
}
